/*
 *   Copyright (c) 2023
 *   All rights reserved.
 */
import React, { useEffect, useState } from "react";
import VmoGridColumn from "../Collections/VmoGrid/VmoGridColumn";
import VmoFormInput from "../Collections/VmoForm/VmoFormInput";
import VmoGrid from "../Collections/VmoGrid/VmoGrid";
import VmoFormField from "../Collections/VmoForm/VmoFormField";
import VmoFormDropdown from "../Collections/VmoForm/VmoFormDropdown";
import VmoCustomDropdown from "../Custom/VmoCustomDropdown";
import VmoFlag from "../Elements/VmoFlag";
import PropTypes from "prop-types";
import { countryCodeDropdownOptions } from "../../data/country-codes";
import { MdPhone } from "react-icons/md";
import { useMemo } from "react";
import VmoTooltip from "./VmoTooltip";
import { AiOutlineInfoCircle } from "react-icons/ai";

export default function VmoPhoneInput({
  width = 8,
  error = "",
  showFlag = false,
  label,
  defaultPhoneCode,
  value,
  onChange = () => {},
  inputProps,
  dropdownProps,
  required,
  tooltipProps,
}) {
  const [phoneValue, setPhoneValue] = useState("");
  const [codeValue, setCodeValue] = useState("");
  const handlePhoneChange = (e) => {
    const newValue = e.target.value;
    // setPhoneValue(newValue);
    onChange({ code: codeValue, phone: newValue });
  };

  const handleCodeChange = (value) => {
    // setCodeValue(value);
    onChange({ code: value, phone: phoneValue });
  };
  const fetchCountryCode = async () => {
    await fetch("https://ipapi.co/json/")
      .then((response) => response.json())
      .then((data) => {
        setCodeValue(data.country_calling_code);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };
  useEffect(() => {
    if (value) {
      setPhoneValue(value.phone ?? "");
      setCodeValue(value.code ?? "");
      return;
    }
  }, [value]);
  useEffect(() => {
    if (!defaultPhoneCode && value && !value.code) {
      fetchCountryCode();
    }
  }, [defaultPhoneCode, value]);

  // Memoize the values to prevent unnecessary re-renders
  const memoizedPhoneValue = useMemo(() => value?.phone ?? "", [value]);
  const memoizedCodeValue = useMemo(() => value?.code ?? "", [value]);
  function _renderInputWithFlag() {
    return (
      <React.Fragment>
        <VmoGridColumn width={width}>
          <VmoFormField inline className="phone-no custom-phone-code">
            <VmoFormInput
              type="text"
              fluid
              icon={<MdPhone />}
              onChange={handlePhoneChange}
              iconPosition="left"
              value={memoizedPhoneValue}
              error={error ? error : false}
              {...inputProps}
            />
            <VmoCustomDropdown
              dropdownProps={{
                inline: true,
                tabIndex: -1,
                ...dropdownProps,
              }}
              value={memoizedCodeValue}
              onClick={handleCodeChange}
              input={{ placeholder: "Search..." }}
              options={countryCodeDropdownOptions}
            />
            <VmoFlag
              className="dd-flag"
              name={
                countryCodeDropdownOptions
                  ?.find((item) => item.value === codeValue)
                  ?.flag.toLowerCase() || "au"
              }
            />
            <span className="country-code">{codeValue}</span>
          </VmoFormField>
        </VmoGridColumn>
      </React.Fragment>
    );
  }
  function _renderLayout() {
    const search = (event, searchQuery) => {
      if (searchQuery === "") {
        return event;
      }
      const r = event.filter((o) => {
        return o.key.toLowerCase().includes(searchQuery.toLowerCase());
      });
      return r;
    };
    return (
      <VmoFormField required={required}>
        <div className="info-header">
          <label>{label}</label>
          {Object.keys(tooltipProps).length ? (
            <VmoTooltip trigger={<AiOutlineInfoCircle />} {...tooltipProps} />
          ) : null}
        </div>
        <VmoGrid>
          <VmoGridColumn width={5}>
            <VmoFormDropdown
              fluid
              search={search}
              selection
              onChange={(e, { value }) => handleCodeChange(value)}
              value={memoizedCodeValue}
              className="countryCode"
              options={countryCodeDropdownOptions}
              error={error ? true : false}
              {...dropdownProps}
            />
          </VmoGridColumn>
          <VmoGridColumn width={11}>
            <VmoFormInput
              fluid
              value={memoizedPhoneValue}
              required
              onChange={handlePhoneChange}
              error={error ? true : false}
              {...inputProps}
            />
          </VmoGridColumn>
        </VmoGrid>
        {error && (
          <span
            style={{ color: "#9F3A38", fontSize: "12px", lineHeight: "8px" }}
          >
            {error}
          </span>
        )}
      </VmoFormField>
    );
  }
  return (
    <React.Fragment>
      {showFlag ? _renderInputWithFlag() : _renderLayout()}
    </React.Fragment>
  );
}

VmoPhoneInput.defaultProps = {
  width: 8,
  error: "",
  showFlag: false,
  label: "",
  defaultPhoneCode: null,
  onChange: () => {},
  inputProps: {},
  dropdownProps: {},
  required: false,
  tooltipProps: {},
};

VmoPhoneInput.propTypes = {
  width: PropTypes.number,
  error: PropTypes.string,
  showFlag: PropTypes.bool,
  label: PropTypes.string,
  defaultPhoneCode: PropTypes.string,
  onChange: PropTypes.func,
  inputProps: PropTypes.object,
  dropdownProps: PropTypes.object,
  required: PropTypes.bool,
  tooltipProps: PropTypes.object,
};

VmoPhoneInput.propTypes.label = (props, propName, componentName) => {
  if (!props.showFlag && !props[propName]) {
    return new Error(
      `Prop \`${propName}\` is required when \`showFlag\` is false in ${componentName}.`
    );
  }
};
